// Cookie-consent card (opt-in, founder-approved 2026-08-02). Design intent:
// easy to accept, honest, zero dark patterns — Accept is the visual primary,
// "No thanks" is a real one-tap choice at readable contrast, and ignoring it
// costs the visitor nothing (analytics stays cookieless-anonymous, exactly the
// pre-rework behavior). Shows once: any choice dismisses it permanently
// (changeable later on /legal/privacy). Enters ~1s after load so it never
// competes with the hero; slides up 12px with the site's ease-out; exits
// faster than it enters. Reduced motion = opacity-only.
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getConsent } from '../consent';
import { phSetConsent } from '../posthog';
import { isExcluded } from '../track';
import { MetalBtn } from './primitives';

export function ConsentBanner() {
  const { pathname } = useLocation();
  const [phase, setPhase] = useState<'hidden' | 'in' | 'out'>('hidden');
  const [gone, setGone] = useState(
    // decided visitors, excluded devices, and the exclusion page itself never see it
    () => getConsent() !== null || isExcluded(),
  );

  const ownChoice = useRef(false);
  useEffect(() => {
    if (gone) return;
    const t = window.setTimeout(() => setPhase('in'), 1000);
    // choice made elsewhere (privacy-page prefs) → this banner is obsolete;
    // the banner's own buttons exit through choose() with its animation
    const onChanged = () => { if (!ownChoice.current) setGone(true); };
    window.addEventListener('cr-consent-changed', onChanged);
    return () => { window.clearTimeout(t); window.removeEventListener('cr-consent-changed', onChanged); };
  }, [gone]);

  if (gone || pathname === '/notme') return null;

  const choose = (granted: boolean) => {
    ownChoice.current = true;
    phSetConsent(granted ? 'granted' : 'denied');
    setPhase('out');
    window.setTimeout(() => setGone(true), 180);
  };

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const shown = phase === 'in';

  return (
    <aside
      role="region"
      aria-label="Cookie consent"
      style={{
        position: 'fixed', left: 16, right: 16, bottom: 16, zIndex: 80,
        maxWidth: 400, marginRight: 'auto',
        padding: '16px 18px', borderRadius: 14,
        background: 'linear-gradient(180deg, rgba(16,16,20,0.97), rgba(9,9,12,0.97))',
        border: '1px solid rgba(255,255,255,0.10)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 20px 50px rgba(0,0,0,0.6)',
        opacity: shown ? 1 : 0,
        transform: reduced ? 'none' : shown ? 'translateY(0)' : 'translateY(12px)',
        pointerEvents: shown ? 'auto' : 'none',
        transition: shown
          ? 'opacity 260ms var(--ease-out), transform 260ms var(--ease-out)'
          : 'opacity 160ms ease-out, transform 160ms ease-out',
      }}
    >
      <span className="cr-label" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--signal-400)' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--signal-400)', boxShadow: '0 0 8px var(--signal-glow)' }}></span>
        Cookies
      </span>
      <p style={{ margin: '10px 0 14px', fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg-2)' }}>
        Can we set one cookie? It lets our stats recognize returning visitors.
      </p>
      {/* Decline gets Accept's box metrics + body-copy contrast (quiet ≠ disadvantaged);
          44px targets on touch come from .crw-consent-actions in site.css */}
      <div className="crw-consent-actions">
        <MetalBtn size="sm" onClick={() => choose(true)}>Accept</MetalBtn>
        <button onClick={() => choose(false)} className="crw-linkbtn"
          style={{ padding: '8px 16px', fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fg-2)' }}>
          Decline
        </button>
        <Link to="/legal/privacy" style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--signal-400)', textDecoration: 'none' }}>
          Privacy →
        </Link>
      </div>
    </aside>
  );
}
